import { useRef, useState } from "react";
import { Upload, Loader2 } from "lucide-react";
import { uploadImage, uploadVideo, fileUrl } from "../lib/api";
import { toast } from "sonner";

/**
 * Compact upload widget for image or video files.
 * Props:
 *  - kind: 'image' | 'video'
 *  - value: current URL/path (used to show preview)
 *  - onChange: (newUrl:string) => void
 *  - accept: optional override for input accept
 *  - testid: base testid
 */
export function FileUploader({ kind = "image", value = "", onChange, accept, testid = "uploader" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const inputAccept = accept || (kind === "image" ? "image/*" : "video/*");

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setProgress(0);
    try {
      const uploader = kind === "video" ? uploadVideo : uploadImage;
      const res = await uploader(file, setProgress);
      onChange?.(res.url); // relative path like "/api/files/{id}"
      toast.success(`${kind === "video" ? "Video" : "Image"} uploaded`);
    } catch (err) {
      const msg = err?.response?.data?.detail || `Upload failed`;
      toast.error(msg);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const previewUrl = fileUrl(value);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4">
        {kind === "image" ? (
          <div
            className="w-24 h-24 rounded-2xl bg-muted border border-border overflow-hidden flex items-center justify-center shrink-0"
            data-testid={`${testid}-preview`}
          >
            {previewUrl ? (
              <img src={previewUrl} alt="preview" className="w-full h-full object-cover" />
            ) : (
              <Upload className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
            )}
          </div>
        ) : (
          <div
            className="w-32 h-24 rounded-2xl bg-black text-white border border-border overflow-hidden flex items-center justify-center shrink-0 text-xs"
            data-testid={`${testid}-preview`}
          >
            {previewUrl ? "Video ready" : "No video"}
          </div>
        )}
        <div className="flex-1 min-w-0 space-y-2">
          <label
            className={`inline-flex items-center gap-2 cursor-pointer bg-secondary text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-slate-800 transition-colors ${
              busy ? "opacity-60 pointer-events-none" : ""
            }`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {busy ? `Uploading ${progress}%` : `Upload ${kind}`}
            <input
              ref={inputRef}
              type="file"
              accept={inputAccept}
              onChange={onFile}
              className="hidden"
              data-testid={`${testid}-input`}
            />
          </label>
          {value && !busy && (
            <button
              type="button"
              onClick={() => onChange?.("")}
              data-testid={`${testid}-clear`}
              className="ml-2 text-sm text-destructive hover:underline"
            >
              Remove
            </button>
          )}
          <div className="text-xs text-muted-foreground">
            {kind === "image" ? "PNG, JPG, WebP or SVG. Max 8 MB." : "MP4, WebM, MOV or OGG. Max 100 MB."}
          </div>
        </div>
      </div>
    </div>
  );
}
